import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ConnectionHandler } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { format, startOfDay } from "date-fns"
import { isEqual } from "es-toolkit/predicate"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { toast } from "@/hooks/useToast"
import CreateEditNewsForm from "@/modules/admin/news/components/news-form"
import { combineDateAndTime } from "@/modules/admin/news/helpers/news-helper"
import { newsFormSchema } from "@/modules/admin/news/schema/news-form-schema"
import { NewsFragment$data } from "@/modules/admin/news/services/__generated__/NewsFragment.graphql"
import { CreateNewsInput } from "@/modules/admin/news/services/__generated__/useCreateNewsMutation.graphql"
import { useCreateNewsNotificationMutation } from "@/modules/admin/news/services/useCreateNewsMutation"
import { NewsConnectionKey } from "@/modules/admin/news/services/useNewsQuery"
import { useUpdateNewsNotificationMutation } from "@/modules/admin/news/services/useUpdateNewsMutation"
import { NewsForm, NewsFormType } from "@/modules/admin/news/types"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { DisplayFor } from "@/types/__generated__/union-types"

interface NewsFormDialogProps {
  onClose: () => void
  typeForm: NewsFormType | null
  newsData: NewsFragment$data | null
}

const defaultDisplayFor: DisplayFor[] = [
  "CALL_CENTER_AGENT",
  "CUSTOMER_SERVICE_CENTER",
  "DOCTOR",
  "NURSING_PROFESSIONAL",
]

const NewsFormDialog = ({ onClose, typeForm, newsData }: NewsFormDialogProps) => {
  const { t } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const editNewsData = typeForm === "EDIT" ? newsData : null

  const form = useForm<NewsForm>({
    resolver: zodResolver(newsFormSchema),
    defaultValues: {
      criticalityLevel: editNewsData?.criticalityLevel ?? "INFO",
      displayFor: (editNewsData?.displayFor.map((item) => item) ?? defaultDisplayFor).sort(),
      startDate: startOfDay(editNewsData?.startTime ?? new Date()),
      startTime: format(editNewsData?.startTime ?? new Date(), "HH:mm"),
      ...(typeForm === "EDIT" &&
        editNewsData?.endTime && {
          endDate: startOfDay(editNewsData?.endTime),
          endTime: format(editNewsData?.endTime, "HH:mm"),
        }),
      germanContent: editNewsData?.germanContent ?? "",
      englishContent: editNewsData?.englishContent ?? "",
      frenchContent: editNewsData?.frenchContent ?? "",
      italianContent: editNewsData?.italianContent ?? "",
    },
  })
  const [createNews, isCreating] = useCreateNewsNotificationMutation()
  const [updateNews, isUpdating] = useUpdateNewsNotificationMutation()

  const {
    handleSubmit,
    formState: { defaultValues },
  } = form

  const handleSaveNews = (data: NewsForm) => {
    const isEdited = !isEqual(defaultValues, data)
    if (isEdited) {
      const payloadNewsForm: CreateNewsInput = {
        criticalityLevel: data.criticalityLevel,
        englishContent: data.englishContent,
        germanContent: data.germanContent,
        frenchContent: data.frenchContent,
        italianContent: data.italianContent,
        displayFor: data.displayFor,
        startTime: combineDateAndTime(data.startDate, data.startTime),
        endTime: data.endDate && data.endTime ? combineDateAndTime(data.endDate, data.endTime) : null,
      }
      if (typeForm === "EDIT") {
        updateNews({
          variables: {
            input: {
              id: editNewsData?.id ?? "",
              criticalityLevel: payloadNewsForm.criticalityLevel,
              displayFor: payloadNewsForm.displayFor,
              endTime: payloadNewsForm.endTime,
              englishContent: payloadNewsForm.englishContent,
              germanContent: payloadNewsForm.germanContent,
              frenchContent: payloadNewsForm.frenchContent,
              italianContent: payloadNewsForm.italianContent,
              startTime: payloadNewsForm.startTime,
            },
          },
          onCompleted: (response) => {
            const errMessage = response.updateNews?.errors?.[0]?.message
            if (errMessage) {
              toast({
                description: errMessage,
              })
            } else {
              toast({
                description: t("admin.news.editedSuccessfully"),
              })
              onClose()
            }
          },
          onError: (error: Error) => {
            console.error(error)
          },
        })
      } else {
        const connectionToAdd = ConnectionHandler.getConnectionID("client:root", NewsConnectionKey)
        createNews({
          variables: {
            input: payloadNewsForm,
            connections: [connectionToAdd],
          },
          onCompleted: (response) => {
            const errMessage = response.createNews?.errors?.[0]?.message
            if (errMessage) {
              toast({
                description: errMessage,
              })
            } else {
              toast({
                description: t("admin.news.createdSuccessfully"),
              })
              onClose()
            }
          },
          onError: (error: Error) => {
            console.error(error)
          },
        })
      }
    }
  }

  const handleDiscard = () => {
    if (isEqual(defaultValues, form.getValues())) {
      onClose()
      return
    }
    confirm({
      title: t("common.confirmation.discardUnsavedChanges"),
      content: t("common.confirmation.unsavedChangesWillBeLostPermanently"),
      cancelText: t("common.confirmation.keepEditing"),
      confirmText: t("common.confirmation.discard"),
      onConfirm: () => {
        onClose()
        cancel()
      },
    })
  }

  return (
    <Dialog open onOpenChange={handleDiscard}>
      <DialogContentCustom className="min-w-200.5" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{typeForm === "EDIT" ? t("admin.news.editNews") : t("admin.news.addPlannedNews")}</DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <CreateEditNewsForm />
        </FormProvider>

        <DialogFooter className="flex p-6 pt-4">
          <Button className="rounded-[20px] px-6 py-2.5" variant="secondary" onClick={handleDiscard}>
            {t("common.button.cancel")}
          </Button>
          <Button
            variant="primary"
            className="ml-0 rounded-[20px] px-6 py-2.5"
            isLoading={isCreating || isUpdating}
            onClick={handleSubmit(handleSaveNews)}>
            {t("common.button.save")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { NewsFormDialog }
